import React, { Fragment, useEffect, useState } from 'react';
import PaymentServices from '../../Api/Payment/PaymentServices';
import Colors from '../../Assets/Styles/Colors';
import { Autocomplete, Box, Button, Chip, Collapse, FormControl, Grid, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { Edit, FilterAltTwoTone, KeyboardArrowLeft, KeyboardArrowRight, RemoveRedEye } from '@mui/icons-material';
import BreadCrumbs from '../../Components/BreadCrumbs/BreadCrumbs';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import AuthServices from '../../Api/Auth/AuthServices';
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.module.css"
import "../../../App.css"
import { useTheme } from '@emotion/react';

const tableHead = [
  "Deposit By",
  "Type",
  "Amount",
  "Deposit At",
  "Description"
];

function DataFilter(props) {
  const {
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    sortData,
  } = props;
  const [filterCollapse, setFilterCollapse] = useState(true);

  const filterData = () => {
    sortData(fromDate && new Date(fromDate), toDate && new Date(toDate));
  };

  const clearData = () => {
    setFromDate(null);
    setToDate(null);
    sortData();
  };

  return (
    <Box sx={{ bgcolor: Colors.white, p: 2, borderRadius: "12px", mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body1" fontWeight="bold" color="initial">
          Filters
        </Typography>
        <IconButton
          onClick={() => setFilterCollapse(!filterCollapse)}
          color="primary"
        >
          <FilterAltTwoTone sx={{ color: Colors.primary }} />
        </IconButton>
      </Box>

      <Collapse in={filterCollapse}>
        <Grid container spacing={2} alignItems={"flex-end"}>
          <Grid item md={6}>
            <DatePicker
              showIcon
              toggleCalendarOnIconClick
              selected={fromDate}
              onChange={(update) => {
                setFromDate(update);
              }}
              isClearable={true}
              placeholderText="Applied Date (From)"
              icon={
                <svg
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  height="1em"
                  width="1em"
                >
                  <path d="M4 .5a.5.5 0 00-1 0V1H2a2 2 0 00-2 2v1h16V3a2 2 0 00-2-2h-1V.5a.5.5 0 00-1 0V1H4V.5zM16 7V5H0v5h5a1 1 0 110 2H0v2a2 2 0 002 2h12a2 2 0 002-2V9h-6a1 1 0 110-2h6z" />
                </svg>
              }
            />
          </Grid>

          <Grid item md={6}>
            <DatePicker
              showIcon
              toggleCalendarOnIconClick
              selected={toDate}
              onChange={(update) => {
                setToDate(update);
              }}
              isClearable={true}
              placeholderText="Applied Date (To)"
              icon={
                <svg
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  height="1em"
                  width="1em"
                >
                  <path d="M4 .5a.5.5 0 00-1 0V1H2a2 2 0 00-2 2v1h16V3a2 2 0 00-2-2h-1V.5a.5.5 0 00-1 0V1H4V.5zM16 7V5H0v5h5a1 1 0 110 2H0v2a2 2 0 002 2h12a2 2 0 002-2V9h-6a1 1 0 110-2h6z" />
                </svg>
              }
            />
          </Grid>

          <Grid item xs={12} container columnSpacing={2} justifyContent="flex-end">
            <Button
              onClick={clearData}
              variant="outlined"
              sx={{
                mx: 1,
                textTransform: "capitalize",
                minWidth: "100px",
                boxShadow:
                  `0px 3px 1px -2px rgb(0 0 0 / 20%), 
                  0px 2px 2px 0px rgb(0 0 0 / 14%),
                  0px 1px 5px 0px rgb(0 0 0 / 12%)`,
              }}
            >
              Clear
            </Button>
            <Button
              onClick={filterData}
              variant="contained"
              sx={{
                textTransform: "capitalize",
                minWidth: "100px",
                boxShadow:
                  `0px 3px 1px -2px rgb(0 0 0 / 20%),
                  0px 2px 2px 0px rgb(0 0 0 / 14%),
                  0px 1px 5px 0px rgb(0 0 0 / 12%)`,
              }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
}

function TablePaginationActions(props) {
  const theme = useTheme;

  const { count, page, rowsPerPage, onPageChange } = props;

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
    </Box>
  );
}

function EscrowAccountLedger() {
  const [escrowAccounts, setEscrowAccounts] = useState([]);
  const [count, setCount] = useState("");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const navigate = useNavigate();

  const getEscrowAccountLedger = async (page, limit, role, adId, userId, from, to) => {
    try {
      const result = await PaymentServices.getEscrowAccountLedger(
        page ? page : 1,
        limit ? limit : 10,
        role ? role : "",
        adId ? adId : "",
        userId ? userId : "",
        from ? from : "",
        to ? to : ""
      );
      if (result.responseCode == 200) {
        setEscrowAccounts(result?.data?.escrowAccountLedger);
        setCount(result?.data.totalCount);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    getEscrowAccountLedger(newPage + 1, rows, "", "", "", fromDate, toDate);
  };

  const handleChangeRowsPerPage = (event) => {
    setRows(Math.floor(event.target.value));
    setPage(0);
    getEscrowAccountLedger(page, event.target.value, "", "", "", fromDate, toDate);
  };

  const sort = (from, to) => {
    getEscrowAccountLedger(page, rows, "", "", "", from, to)
  }

  useEffect(() => {
    getEscrowAccountLedger();
  }, []);

  return (
    <Fragment>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{
          background: Colors.primaryGradient,
          p: 2,
          borderRadius: "12px",
        }}
      >
        <Grid item xs={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <IconButton onClick={() => navigate(-1)}>
              <KeyboardArrowLeft sx={{ color: Colors.white }} />
            </IconButton>
            <Typography variant="body1" fontWeight="bold" sx={{ color: Colors.white }}>
              Escrow Accounts
            </Typography>
          </Box>
        </Grid>
        <Grid
          item
          xs={6}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <BreadCrumbs currentPage={["Escrow Accounts"]} />
        </Grid>
      </Grid>
      <DataFilter
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
        sortData={(from, to) => sort(from, to)}
      />
      <TableContainer
        component={Paper}
        sx={{
          // width: "calc(100vw - 320px)",
          "&::-webkit-scrollbar": {
            bgcolor: Colors.white,
            height: "8px",
            borderRadius: "10px",
            cursor: "pointer",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: Colors.primary,
            borderRadius: "10px",
            cursor: "pointer",
          },
          "&.MuiPaper-root": {
            borderRadius: "12px",
            mt: 2,
            boxShadow: 0,
          },
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow sx={{ bgcolor: Colors.lightGray }}>
              {tableHead.map((item, index) => (
                <TableCell key={index}>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      // textAlign: item == "Posted By" ? "center" : item == "Actions" && "end",
                      width: "130px",
                    }}
                  >
                    {item}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {escrowAccounts.map((row, index) => (
              <TableRow key={index}>
                <TableCell>
                  {row?.user?.name}
                </TableCell>
                <TableCell>
                  {row?.type}
                </TableCell>
                <TableCell>
                  {row?.amount}
                </TableCell>
                <TableCell>
                  {moment(row?.createdAt).format("DD-MM-YYYY")}
                </TableCell>
                <TableCell>
                  {row?.description}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Table sx={{ marginTop: "12px" }}>
        <TablePagination
          sx={{
            borderBottom: "none",
            bgcolor: Colors.white,
            borderRadius: "12px",
          }}
          rowsPerPageOptions={[10, 20, 30]}
          colSpan={12}
          count={+count}
          rowsPerPage={rows}
          page={page}
          SelectProps={{
            inputProps: {
              "aria-label": "rows per page",
            },
            native: true,
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      </Table>
    </Fragment>
  )
}

export default EscrowAccountLedger